import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslationChange } from 'i18nano'
import { Cell, Section } from '@telegram-apps/telegram-ui'
import { Check, Copy, LogOut, Trash2, UserRound } from 'lucide-react'
import { Fragment, useState } from 'react'
import { toast } from 'sonner'
import { LanguageSwitcher } from 'features/language-switcher'
import { useAdminTrpc } from 'shared/api'
import { Button } from 'shared/components/ui/button'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from 'shared/components/ui/card'
import { Field, FieldLabel } from 'shared/components/ui/field'
import { Input } from 'shared/components/ui/input'
import {
	categoryNamesChanged,
	categoryNamesPayload,
	getLocalizedCategoryName,
	type CategoryNames,
} from 'shared/lib/categories'
import { useTranslate } from 'shared/lib/i18n'
import { hasAdminPermission } from 'shared/lib/permissions/permissions'
import { clearAuthSession } from 'shared/lib/auth/session'
import { isTMA } from '@tma.js/sdk-react'
import { useAuthStore } from 'shared/store/auth'
import { getAccountIdentity } from '../lib/account'
import { getAdminSettingsSectionKeys } from '../lib/sections'
import { Separator } from 'shared/components/ui/separator'

interface PaymentCard {
	bankName: string
	cardNumber: string
	holderName: string
}

interface GeneralSettingsDraft {
	rate: string
	commission: string
	cards: PaymentCard[]
}

function initialSettingsDraft(settings?: {
	usdToUzsRate: number
	commissionPercent: number
	paymentCards: PaymentCard[]
}): GeneralSettingsDraft {
	return {
		rate: settings ? String(settings.usdToUzsRate) : '',
		commission: settings ? String(settings.commissionPercent) : '',
		cards: settings?.paymentCards ?? [],
	}
}

export function AdminSettings() {
	const user = useAuthStore((state) => state.user)

	const canManageSettings = user
		? hasAdminPermission(user, 'super_admin')
		: false
	const canManageCategories = user
		? hasAdminPermission(user, 'lots_admin')
		: false
	const { t, prefixT } = useTranslate('pages.adminSettings')
	const { label: identity, copyValue } = getAccountIdentity(user)
	const sectionKeys = getAdminSettingsSectionKeys(
		canManageSettings,
		canManageCategories,
	)

	function copyIdentity() {
		if (!copyValue) return
		void navigator.clipboard?.writeText(copyValue)
		toast.success(t('common.copied'))
	}

	return (
		<div className='space-y-4 p-4'>
			<h1 className='text-xl font-semibold text-foreground'>
				{prefixT('title')}
			</h1>
			<Section header={prefixT('account')}>
				<div className='flex items-center'>
					<Cell
						Component='button'
						type='button'
						className='flex-1'
						aria-label={
							copyValue ? `${identity}. ${t('common.copy')}` : undefined
						}
						onClick={copyIdentity}
						before={<UserRound aria-hidden size={22} />}
						after={copyValue ? <Copy aria-hidden size={20} /> : undefined}
					>
						{identity}
					</Cell>

					{!isTMA() && (
						<Button
							variant='destructive'
							size='icon-lg'
							onClick={() => clearAuthSession('browser-admin')}
						>
							<LogOut />
						</Button>
					)}
				</div>
				<LanguageSwitcher />
			</Section>

			{sectionKeys.map((sectionKey) => (
				<Fragment key={sectionKey}>
					<Separator />
					{sectionKey === 'general' ? (
						<GeneralSettingsSection />
					) : (
						<CategoriesCard />
					)}
				</Fragment>
			))}
		</div>
	)
}

function GeneralSettingsSection() {
	const trpc = useAdminTrpc()
	const { data: settings, isLoading } = useQuery(
		trpc.settings.Get.queryOptions(),
	)
	const update = useMutation(trpc.settings.Update.mutationOptions())

	const [draft, setDraft] = useState<GeneralSettingsDraft | null>(null)
	const { t, prefixT } = useTranslate('pages.adminSettings')
	const initialDraft = initialSettingsDraft(settings)
	const form = draft ?? initialDraft

	function updateDraft(
		update: (current: GeneralSettingsDraft) => GeneralSettingsDraft,
	) {
		setDraft((current) => update(current ?? initialDraft))
	}

	function setCard(i: number, patch: Partial<PaymentCard>) {
		updateDraft((current) => ({
			...current,
			cards: current.cards.map((card, index) =>
				index === i ? { ...card, ...patch } : card,
			),
		}))
	}

	async function handleSave() {
		try {
			await update.mutateAsync({
				usdToUzsRate: Number(form.rate),
				commissionPercent: Number(form.commission),
				paymentCards: form.cards,
			})
			toast.success(prefixT('saved'))
		} catch {
			toast.error(prefixT('saveFailed'))
		}
	}

	if (isLoading) {
		return <div className='p-4 text-muted-foreground'>{prefixT('loading')}</div>
	}

	return (
		<>
			<Card>
				<CardHeader>
					<CardTitle>{prefixT('rateCommission')}</CardTitle>
				</CardHeader>
				<CardContent className='space-y-4'>
					<Field>
						<FieldLabel htmlFor='rate'>{prefixT('rate')}</FieldLabel>
						<Input
							id='rate'
							type='number'
							inputMode='numeric'
							value={form.rate}
							onChange={(e) =>
								updateDraft((current) => ({ ...current, rate: e.target.value }))
							}
						/>
					</Field>
					<Field>
						<FieldLabel htmlFor='commission'>
							{prefixT('commission')}
						</FieldLabel>
						<Input
							id='commission'
							type='number'
							inputMode='numeric'
							value={form.commission}
							onChange={(e) =>
								updateDraft((current) => ({
									...current,
									commission: e.target.value,
								}))
							}
						/>
					</Field>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>{prefixT('paymentCards')}</CardTitle>
				</CardHeader>
				<CardContent className='space-y-4'>
					{form.cards.map((card, i) => (
						<div
							key={i}
							className='space-y-2 rounded-lg border border-border p-3'
						>
							<div className='flex items-center justify-between'>
								<span className='text-sm text-muted-foreground'>
									{prefixT('card', { index: String(i + 1) })}
								</span>
								<Button
									variant='ghost'
									size='icon'
									onClick={() =>
										updateDraft((current) => ({
											...current,
											cards: current.cards.filter((_, index) => index !== i),
										}))
									}
								>
									<Trash2 className='size-4 text-destructive' />
								</Button>
							</div>
							<Input
								placeholder={prefixT('bank')}
								value={card.bankName}
								onChange={(e) => setCard(i, { bankName: e.target.value })}
							/>
							<Input
								placeholder={prefixT('cardNumber')}
								value={card.cardNumber}
								onChange={(e) => setCard(i, { cardNumber: e.target.value })}
							/>
							<Input
								placeholder={prefixT('holder')}
								value={card.holderName}
								onChange={(e) => setCard(i, { holderName: e.target.value })}
							/>
						</div>
					))}
					<Button
						variant='outline'
						onClick={() =>
							updateDraft((current) => ({
								...current,
								cards: [
									...current.cards,
									{ bankName: '', cardNumber: '', holderName: '' },
								],
							}))
						}
					>
						{prefixT('addCard')}
					</Button>
				</CardContent>
			</Card>

			<Button onClick={handleSave} disabled={update.isPending}>
				{update.isPending ? t('common.saving') : t('common.save')}
			</Button>
		</>
	)
}

function CategoriesCard() {
	const trpc = useAdminTrpc()
	const { data: categories } = useQuery(trpc.categories.List.queryOptions())
	const create = useMutation(trpc.categories.Create.mutationOptions())
	const rename = useMutation(trpc.categories.Rename.mutationOptions())
	const remove = useMutation(trpc.categories.Delete.mutationOptions())

	const [newNames, setNewNames] = useState<CategoryNames>({
		nameRu: '',
		nameUz: '',
	})
	// Draft names keyed by category id — an entry exists only while edited.
	const [drafts, setDrafts] = useState<Record<string, CategoryNames>>({})
	const { t, prefixT } = useTranslate('pages.adminSettings')
	const { lang } = useTranslationChange()

	async function handleCreate() {
		const payload = categoryNamesPayload(newNames)
		if (!payload) {
			toast.error(prefixT('categoryNamesRequired'))
			return
		}
		try {
			await create.mutateAsync(payload)
			setNewNames({ nameRu: '', nameUz: '' })
		} catch {
			toast.error(prefixT('createCategoryFailed'))
		}
	}

	async function handleRename(id: string) {
		const payload = drafts[id] && categoryNamesPayload(drafts[id])
		if (!payload) {
			toast.error(prefixT('categoryNamesRequired'))
			return
		}
		try {
			await rename.mutateAsync({ id, ...payload })
			setDrafts((current) => {
				const next = { ...current }
				delete next[id]
				return next
			})
		} catch {
			toast.error(prefixT('renameCategoryFailed'))
		}
	}

	async function handleDelete(id: string, names: CategoryNames) {
		// Deleting also unlinks the category from every lot.
		const name = getLocalizedCategoryName(names, lang)
		if (!window.confirm(prefixT('deleteCategoryConfirm', { name }))) return
		try {
			await remove.mutateAsync({ id })
		} catch {
			toast.error(prefixT('deleteCategoryFailed'))
		}
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>{prefixT('categories')}</CardTitle>
			</CardHeader>
			<CardContent className='space-y-3'>
				{(categories ?? []).map((cat) => {
					const original = { nameRu: cat.nameRu, nameUz: cat.nameUz }
					const draft = drafts[cat._id] ?? original
					const canRename =
						drafts[cat._id] !== undefined &&
						categoryNamesChanged(draft, original)
					return (
						<div
							key={cat._id}
							className='space-y-2 rounded-lg border border-border p-3'
						>
							<div className='grid gap-2 sm:grid-cols-2'>
								<Field>
									<FieldLabel>{prefixT('categoryNameRu')}</FieldLabel>
									<Input
										value={draft.nameRu}
										onChange={(e) =>
											setDrafts((current) => ({
												...current,
												[cat._id]: { ...draft, nameRu: e.target.value },
											}))
										}
									/>
								</Field>
								<Field>
									<FieldLabel>{prefixT('categoryNameUz')}</FieldLabel>
									<Input
										value={draft.nameUz}
										onChange={(e) =>
											setDrafts((current) => ({
												...current,
												[cat._id]: { ...draft, nameUz: e.target.value },
											}))
										}
									/>
								</Field>
							</div>
							<div className='flex justify-end gap-2'>
								<Button
									variant='outline'
									size='icon'
									disabled={rename.isPending || !canRename}
									onClick={() => void handleRename(cat._id)}
								>
									<Check className='size-4' />
								</Button>
								<Button
									variant='ghost'
									size='icon'
									disabled={remove.isPending}
									onClick={() => void handleDelete(cat._id, original)}
								>
									<Trash2 className='size-4 text-destructive' />
								</Button>
							</div>
						</div>
					)
				})}
				<form
					className='space-y-2 rounded-lg border border-border p-3'
					onSubmit={(e) => {
						e.preventDefault()
						void handleCreate()
					}}
				>
					<div className='grid gap-2 sm:grid-cols-2'>
						<Field>
							<FieldLabel>{prefixT('categoryNameRu')}</FieldLabel>
							<Input
								value={newNames.nameRu}
								onChange={(e) =>
									setNewNames((current) => ({
										...current,
										nameRu: e.target.value,
									}))
								}
							/>
						</Field>
						<Field>
							<FieldLabel>{prefixT('categoryNameUz')}</FieldLabel>
							<Input
								value={newNames.nameUz}
								onChange={(e) =>
									setNewNames((current) => ({
										...current,
										nameUz: e.target.value,
									}))
								}
							/>
						</Field>
					</div>
					<Button
						type='submit'
						variant='outline'
						disabled={create.isPending || !categoryNamesPayload(newNames)}
					>
						{t('common.add')}
					</Button>
				</form>
			</CardContent>
		</Card>
	)
}
